import React, { useState, useEffect, useCallback } from 'react';
import { getBackendUrl } from '../utils/api';
import storage from '../utils/storage';
import { 
  Brain, 
  TrendingUp, 
  CheckCircle, 
  AlertTriangle, 
  RefreshCw, 
  BookOpen,
  XCircle,
  BarChart3,
  DollarSign,
  Music,
  Zap,
  Database
} from 'lucide-react';

const BACKEND_URL = getBackendUrl();

/**
 * Unified Smart Recognition Widget
 * 
 * Combines Learning Effectiveness + Fingerprint Cache stats
 * Shows:
 * - Auto-resolved rate
 * - Fingerprint cache performance
 * - API calls made & saved (cost tracking)
 * - Learning progress
 */
const LearningEffectivenessWidget = () => {
  const [stats, setStats] = useState(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [hitTrend, setHitTrend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchStats = useCallback(async () => {
    try {
      const token = storage.getItem('token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Cache-Control': 'no-cache'
      };
      
      // Fetch all 3 endpoints in parallel
      const [learningRes, cacheRes, trendRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/dashboard/learning-stats`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/dashboard/fingerprint-cache/stats`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/dashboard/cache-hit-trend?days=7`, { headers })
      ]);
      
      if (learningRes.ok) {
        const learningData = await learningRes.json();
        setStats(learningData);
      }
      
      if (cacheRes.ok) {
        const cacheData = await cacheRes.json();
        setCacheStats(cacheData.cache_stats || cacheData);
      }
      
      if (trendRes.ok) {
        const trendData = await trendRes.json();
        setHitTrend(trendData);
      }
      
      setError(null);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 120000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const handleRefresh = () => {
    setLoading(true);
    fetchStats();
  };

  if (loading && !stats) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
        <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
          <AlertTriangle className="w-5 h-5" />
          <span>Αποτυχία φόρτωσης: {error}</span>
        </div>
        <button 
          onClick={handleRefresh}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Επανάληψη
        </button>
      </div>
    );
  }

  const effectiveness = stats?.effectiveness || {};
  const totals = stats?.totals || {};
  const learning = stats?.learning || {};
  const blocking = stats?.blocking || {};
  
  // Cache stats
  const cachedSongs = cacheStats?.total_entries || 0;
  const identifiedSongs = cacheStats?.identified_songs || 0;
  const apiCallsSaved = cacheStats?.api_calls_saved || 0;
  const estimatedSavings = cacheStats?.estimated_savings_usd || 0;
  const topCachedSongs = cacheStats?.top_cached_songs || [];
  
  // Calculate API calls made (total - saved)
  // Each identified song required at least one API call initially
  const apiCallsMade = identifiedSongs; // Songs that required AudD API
  const costPerCall = 0.005; // $0.005 per API call (AudD Indie plan)
  const estimatedCost = apiCallsMade * costPerCall;

  // Health status
  const getHealthStatus = () => {
    const percent = effectiveness.auto_resolved_percent || 0;
    if (percent >= 95) return { color: 'green', text: 'Εξαιρετική', emoji: '🟢' };
    if (percent >= 85) return { color: 'yellow', text: 'Καλή', emoji: '🟡' };
    if (percent >= 70) return { color: 'orange', text: 'Μέτρια', emoji: '🟠' };
    return { color: 'red', text: 'Χρειάζεται Προσοχή', emoji: '🔴' };
  };

  const healthStatus = getHealthStatus();

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Brain className="w-7 h-7 text-white" />
            <div>
              <h3 className="text-lg font-bold text-white">Έξυπνη Αναγνώριση</h3>
              <p className="text-purple-200 text-sm">Μάθηση + Fingerprint Cache</p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="p-2 bg-white/20 rounded-lg hover:bg-white/30 transition-colors"
            title="Ανανέωση"
          >
            <RefreshCw className={`w-5 h-5 text-white ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      <div className="p-6 space-y-6">
        {/* Main Score Row */}
        <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-700/30">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
              healthStatus.color === 'green' ? 'bg-green-100 dark:bg-green-900/30' :
              healthStatus.color === 'yellow' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
              healthStatus.color === 'orange' ? 'bg-orange-100 dark:bg-orange-900/30' :
              'bg-red-100 dark:bg-red-900/30'
            }`}>
              <span className="text-2xl font-bold">
                {effectiveness.auto_resolved_percent || 0}%
              </span>
            </div>
            <div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Αυτόματη Αναγνώριση</div>
              <div className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                {healthStatus.emoji} {healthStatus.text}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {(effectiveness.auto_resolved_count || 0).toLocaleString()}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              από {(totals.total_plays || 0).toLocaleString()} plays
            </div>
          </div>
        </div>

        {/* Stats Grid - 2 rows */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {/* Auto Resolved */}
          <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <span className="text-sm font-medium text-green-700 dark:text-green-300">Auto-resolved</span>
            </div>
            <div className="text-2xl font-bold text-green-600">
              {effectiveness.auto_resolved_percent || 0}%
            </div>
            <div className="text-xs text-green-600/70">
              {(effectiveness.auto_resolved_count || 0).toLocaleString()} plays
            </div>
          </div>

          {/* Pending */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4 border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-yellow-600" />
              <span className="text-sm font-medium text-yellow-700 dark:text-yellow-300">Pending</span>
            </div>
            <div className="text-2xl font-bold text-yellow-600">
              {effectiveness.needs_manual_percent || 0}%
            </div>
            <div className="text-xs text-yellow-600/70">
              {(totals.pending_plays || 0).toLocaleString()} plays
            </div>
          </div>

          {/* Cached Songs */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <Database className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">Cached Songs</span>
            </div>
            <div className="text-2xl font-bold text-blue-600">
              {cachedSongs.toLocaleString()}
            </div>
            <div className="text-xs text-blue-600/70">
              {identifiedSongs} identified
            </div>
          </div>

          {/* Aliases */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4 border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-2 mb-2">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium text-purple-700 dark:text-purple-300">Aliases</span>
            </div>
            <div className="text-2xl font-bold text-purple-600">
              {(learning.total_aliases || 0).toLocaleString()}
            </div>
            <div className="text-xs text-purple-600/70">
              {learning.learning_aliases || 0} από μάθηση
            </div>
          </div>
        </div>

        {/* API Costs Section */}
        <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-4 border border-orange-200 dark:border-orange-800">
          <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-orange-600" />
            Κόστος Fingerprint API (AudD)
          </h4>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* API Calls Made */}
            <div className="text-center p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">API Calls (χρέωση)</div>
              <div className="text-xl font-bold text-red-600">{apiCallsMade.toLocaleString()}</div>
              <div className="text-xs text-red-500">~${estimatedCost.toFixed(2)}</div>
            </div>
            
            {/* API Calls Saved */}
            <div className="text-center p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">API Calls Saved</div>
              <div className="text-xl font-bold text-green-600">{apiCallsSaved.toLocaleString()}</div>
              <div className="text-xs text-green-500">~${estimatedSavings.toFixed(2)}</div>
            </div>
            
            {/* Total Cost */}
            <div className="text-center p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Συνολικό Κόστος</div>
              <div className="text-xl font-bold text-orange-600">${estimatedCost.toFixed(2)}</div>
              <div className="text-xs text-gray-500">@ $0.005/call</div>
            </div>
            
            {/* Total Savings */}
            <div className="text-center p-3 bg-white/50 dark:bg-gray-800/50 rounded-lg">
              <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Εξοικονόμηση</div>
              <div className="text-xl font-bold text-green-600">${estimatedSavings.toFixed(2)}</div>
              <div className="text-xs text-green-500">από cache hits</div>
            </div>
          </div>
        </div>

        {/* Learning Progress */}
        <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4">
          <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-purple-600" />
            Πρόοδος Μάθησης
          </h4>

          <div className="space-y-4">
            {/* Πηγές Γνώσης Συστήματος — bar chart per source group.
                Replaces the old "Aliases από μάθηση 28%" ratio metric which
                was misleading: it dropped whenever admins added manual aliases,
                making it look like the system was "forgetting" when in fact
                it was still learning normally. The new view is absolute and
                transparent — you see each source's contribution. */}
            {(() => {
              const breakdown = learning.source_breakdown || {};
              const total = Object.values(breakdown).reduce((a, b) => a + (b || 0), 0);
              const SOURCES = [
                { key: 'csv_upload',          label: '📥 Από CSV',           color: 'bg-blue-500',   tip: 'Pre-loaded από admin uploads' },
                { key: 'metadata_pattern',    label: '🎵 Metadata patterns', color: 'bg-emerald-500', tip: 'Auto-learned από επαναλαμβανόμενα metadata' },
                { key: 'conflict_prevention', label: '🛡️ Conflict prevention', color: 'bg-amber-500', tip: 'Auto-created για να αποτραπούν canonical conflicts' },
                { key: 'fingerprint',         label: '🎯 Fingerprint (AudD)', color: 'bg-purple-500', tip: 'Audio fingerprint matches' },
                { key: 'certified',           label: '⭐ Certified',         color: 'bg-yellow-500', tip: 'Συνδεδεμένα με χειροκίνητα certified entries' },
                { key: 'manual_other',        label: '🔧 Manual / άλλα',     color: 'bg-gray-500',   tip: 'Admin actions ή misc sources' },
              ];
              return (
                <div data-testid="learning-source-breakdown">
                  <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Πηγές Γνώσης ({total.toLocaleString('el-GR')} συνολικά)
                  </div>
                  <div className="space-y-1.5">
                    {SOURCES.map(s => {
                      const cnt = breakdown[s.key] || 0;
                      const pct = total > 0 ? (cnt / total) * 100 : 0;
                      if (cnt === 0) return null;
                      return (
                        <div key={s.key} className="flex items-center gap-2 text-xs" title={s.tip}>
                          <span className="w-32 truncate text-gray-600 dark:text-gray-300">{s.label}</span>
                          <div className="flex-1 bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                            <div className={`${s.color} h-full transition-all duration-500`}
                              style={{ width: `${Math.max(pct, 0.5)}%` }} />
                          </div>
                          <span className="w-20 text-right tabular-nums text-gray-700 dark:text-gray-300">
                            {cnt.toLocaleString('el-GR')}
                          </span>
                          <span className="w-12 text-right tabular-nums text-gray-500">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Νέες αναγνωρίσεις τελευταίων 7 ημερών — απόλυτος αριθμός που
                πάντα μεγαλώνει αν το σύστημα μαθαίνει. Δείχνει per-source. */}
            {(() => {
              const trend = learning.trend_7d || {};
              const total7d = learning.trend_7d_total || 0;
              const SOURCES_7D = [
                { key: 'fingerprint',         label: '🎯 Fingerprint',  color: 'text-purple-600 dark:text-purple-400' },
                { key: 'metadata_pattern',    label: '🎵 Metadata',     color: 'text-emerald-600 dark:text-emerald-400' },
                { key: 'conflict_prevention', label: '🛡️ Conflict',     color: 'text-amber-600 dark:text-amber-400' },
                { key: 'certified',           label: '⭐ Certified',     color: 'text-yellow-600 dark:text-yellow-400' },
                { key: 'csv_upload',          label: '📥 CSV',           color: 'text-blue-600 dark:text-blue-400' },
              ];
              return (
                <div className="pt-3 border-t border-gray-200 dark:border-gray-600" data-testid="learning-trend-7d">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      Τελευταίες 7 ημέρες
                    </div>
                    <div className="text-xs text-gray-500">
                      σύνολο: <span className="font-semibold text-emerald-600">+{total7d.toLocaleString('el-GR')}</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    {SOURCES_7D.map(s => {
                      const v = trend[s.key] || 0;
                      return (
                        <div key={s.key} className="text-center bg-white/60 dark:bg-gray-800/40 rounded p-1.5 border border-gray-200 dark:border-gray-600">
                          <div className={`text-sm font-bold ${s.color}`}>+{v.toLocaleString('el-GR')}</div>
                          <div className="text-[10px] text-gray-500 mt-0.5 truncate">{s.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* Cache Hit Rate — clear "X out of every 100 lookups" scale that
                directly answers: "how often did stored data save us an API call?" */}
            {(() => {
              const ch = learning.cache_hit_rate || {};
              const hitsPer100 = ch.hits_per_100_lookups || 0;
              const totalLookups = ch.total_lookups || 0;
              return (
                <div className="pt-3 border-t border-gray-200 dark:border-gray-600" data-testid="cache-hit-rate-card">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-medium text-gray-700 dark:text-gray-300">
                      🎯 Cache Hit Rate
                    </div>
                    <div className="text-xs text-gray-500">
                      {totalLookups.toLocaleString('el-GR')} συνολικά lookups
                    </div>
                  </div>
                  <div className="bg-white/60 dark:bg-gray-800/40 rounded p-3 border border-gray-200 dark:border-gray-600">
                    <div className="flex items-baseline gap-2 mb-1.5">
                      <span className="text-2xl font-bold text-purple-600">{hitsPer100}</span>
                      <span className="text-sm text-gray-500">στα 100 lookups</span>
                      <span className="ml-auto text-xs text-gray-500">βρέθηκαν από cache</span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 overflow-hidden mb-1.5">
                      <div className="bg-purple-600 h-full transition-all duration-500"
                        style={{ width: `${Math.min(hitsPer100, 100)}%` }} />
                    </div>
                    <div className="flex justify-between text-[11px] text-gray-500">
                      <span>✅ {(ch.cache_hits || 0).toLocaleString('el-GR')} από cache</span>
                      <span>🌐 {(ch.fresh_api_calls || 0).toLocaleString('el-GR')} νέα API calls</span>
                    </div>
                    
                    {/* 7-day Sparkline (if we have history) */}
                    {hitTrend?.datapoints?.length > 1 && (() => {
                      const pts = hitTrend.datapoints;
                      const maxRate = Math.max(...pts.map(p => p.hit_rate_percent), 100);
                      const W = 280; const H = 36;
                      const stepX = W / Math.max(pts.length - 1, 1);
                      const coords = pts.map((p, i) => {
                        const x = i * stepX;
                        const y = H - (p.hit_rate_percent / maxRate) * (H - 4) - 2;
                        return [x, y, p];
                      });
                      const linePath = coords.map(([x, y], i) =>
                        (i === 0 ? `M ${x},${y}` : `L ${x},${y}`)).join(' ');
                      const areaPath = `${linePath} L ${W},${H} L 0,${H} Z`;
                      const last = pts[pts.length - 1];
                      const trend = pts.length >= 2
                        ? pts[pts.length - 1].hit_rate_percent - pts[0].hit_rate_percent
                        : 0;
                      return (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700" data-testid="cache-hit-sparkline">
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-[11px] font-medium text-gray-600 dark:text-gray-400">
                              Τελευταίες {pts.length} ημέρες
                            </div>
                            <div className="flex items-center gap-2 text-[11px]">
                              <span className="text-gray-500">avg {hitTrend.summary?.avg_rate || 0}%</span>
                              <span className={trend >= 0 ? 'text-green-600' : 'text-red-600'}>
                                {trend >= 0 ? '↑' : '↓'} {Math.abs(trend).toFixed(1)}%
                              </span>
                            </div>
                          </div>
                          <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`}
                            className="w-full h-9" preserveAspectRatio="none">
                            <path d={areaPath} fill="rgba(147, 51, 234, 0.15)" />
                            <path d={linePath} stroke="rgb(147, 51, 234)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                            {coords.map(([x, y, p], i) => (
                              <g key={i}>
                                <circle cx={x} cy={y} r="2.5" fill="rgb(147, 51, 234)">
                                  <title>{`${p.date}: ${p.hit_rate_percent}% (${p.hits}h / ${p.calls}c)`}</title>
                                </circle>
                              </g>
                            ))}
                          </svg>
                          <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                            <span>{pts[0].date.slice(5)}</span>
                            <span className="font-medium">σήμερα: {last.hit_rate_percent}%</span>
                            <span>{last.date.slice(5)}</span>
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Empty-state hint when we have only 1 datapoint */}
                    {hitTrend?.datapoints?.length === 1 && (
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 text-[11px] text-gray-500" data-testid="cache-hit-sparkline-empty">
                        📈 Το trend chart χρειάζεται 2+ ημέρες snapshots. Snapshot τρέχει αυτόματα κάθε βράδυ στις 23:55.
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Blocked */}
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600 dark:text-gray-400">Auto-detected blocks</span>
                <span className="font-medium text-red-600">
                  {blocking.auto_blocked || 0} / {blocking.total_blocked_keys || 0}
                </span>
              </div>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                <div 
                  className="bg-red-500 h-2 rounded-full transition-all duration-500"
                  style={{ 
                    width: `${Math.min(
                      ((blocking.auto_blocked || 0) / (blocking.total_blocked_keys || 1)) * 100, 
                      100
                    )}%` 
                  }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* Top Cached Songs */}
        {topCachedSongs.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-700/30 rounded-lg p-4">
            <h4 className="font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <Music className="w-5 h-5 text-indigo-600" />
              Top Cached Songs
            </h4>
            <div className="space-y-2">
              {topCachedSongs.slice(0, 5).map((song, idx) => (
                <div key={idx} className="flex justify-between items-center text-sm p-2 bg-white dark:bg-gray-800 rounded">
                  <span className="text-gray-700 dark:text-gray-300 truncate">
                    {song.artist} - {song.title}
                  </span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    song.hit_count > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {song.hit_count || 0} hits
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Last Updated */}
        {lastRefresh && (
          <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
            Τελευταία ενημέρωση: {lastRefresh.toLocaleTimeString('el-GR')}
          </div>
        )}
      </div>
    </div>
  );
};

export default LearningEffectivenessWidget;
